import React, { useState, useMemo } from 'react';
import Wrapper from '../../components/Wrapper';
import { Button, Form, InputGroup, Spinner, Table, Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchLogs } from '../../store/features/logSlice'; 

const ViewLogs = () => {
  const dispatch = useDispatch();
  const [searchKeyword, setSearchKeyword] = useState('');
  
  const { status, error, logs, rawData } = useSelector((state) => state.logs);

  const handleSearch = (e) => {
    if ((e.type === 'click' || e.key === 'Enter') && searchKeyword.trim()) {
      dispatch(fetchLogs({ searchKeyword }));
    }
  };

  const formatCellData = (key, value) => {
    if (value === undefined || value === null) return '-';
    
    const timeKeys = ['received_at', 'created_at', 'updated_at', 'payload_created_at', 'payload_updated_at'];
    if (timeKeys.includes(key) && typeof value === 'string') {
       return value.replace('T', ', ').replace('Z', '');
    }
    return value;
  };

  const processTableData = (dataArray) => {
    if (!dataArray || dataArray.length === 0) return { headers: [], rows: [] };

    const allKeys = new Set();
    
    const flattenedData = dataArray.map((item) => {
      const flatObj = {};

      Object.keys(item).forEach((key) => {
        if (key !== 'payload') {
          flatObj[key] = item[key];
          allKeys.add(key);
        }
      });

      if (item.payload) {
        Object.keys(item.payload).forEach((pKey) => {
          const finalKey = flatObj.hasOwnProperty(pKey) ? `payload_${pKey}` : pKey;
          flatObj[finalKey] = item.payload[pKey];
          allKeys.add(finalKey);
        });
      }
      return flatObj;
    });

    // For Sorting
    const columnPriority = [
      'id', 'device_id', 'sample_id', 'value_type', 'sample_type', 'received_at', 'time_stamp',
      'ph', 'ec', 'oc', 'nitrate', 'ammonia', 
      'potassium', 'phosphorus', 'calcium', 'magnesium', 'sulphur', 
      'zinc', 'iron', 'boron', 'copper', 'manganese', 'carbonate', 'bicarbonate', 'tds',
      'created_at', 'updated_at', 'payload_id', 'session_id', 'version'
    ];

    const sortedHeaders = Array.from(allKeys).sort((a, b) => {
      const indexA = columnPriority.indexOf(a);
      const indexB = columnPriority.indexOf(b);

      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });

    return { headers: sortedHeaders, rows: flattenedData };
  };

  const rawTableData = useMemo(() => processTableData(rawData), [rawData]);
  const logsTableData = useMemo(() => processTableData(logs), [logs]);

  const hasData = rawTableData.rows.length > 0 || logsTableData.rows.length > 0;

  const RenderTableSection = ({ title, data }) => {
    if (data.rows.length === 0) return null;

    return (
      <div className='w-100 px-4 mt-4'>
        <h4 className='mb-3 text-secondary'>{title}</h4>
        <div className='shadow-sm p-3 mb-5 bg-body rounded'>
          <Table striped bordered hover responsive className='align-middle text-nowrap'>
            <thead className='table-dark'>
              <tr>
                {data.headers.map((head) => (
                  <th key={head} className="text-uppercase" style={{ fontSize: '0.85rem' }}>
                    {head === 'device_id' ? 'Device ID/ Name' : head.replace('payload_', 'PL ').replace(/_/g, ' ')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, rIndex) => (
                <tr key={row.id || rIndex}>
                  {data.headers.map((colKey, cIndex) => (
                    <td key={`${rIndex}-${cIndex}`}>
                      {formatCellData(colKey, row[colKey])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <Wrapper>
      <div className='d-flex flex-column justify-content-center align-items-center pt-4'>
        <h1>Sample Logs</h1>
        <p className='text-muted'>View raw telemetry data for specific samples</p>

        {/* --- Search Bar --- */}
        <div className='w-100 mt-5'>
          <InputGroup className='mb-3 container-md w-50' onChange={(e) => setSearchKeyword(e.target.value)} onKeyDown={handleSearch}>
            <Form.Control
              placeholder='Enter Sample ID'
              aria-label='Enter Sample ID'
              value={searchKeyword}
            />
            <Button
              variant='dark'
              className='rounded-end rounded-start-0'
              onClick={handleSearch}
              disabled={status === 'loading'}
            >
              {status === 'loading' && <Spinner size='sm' className='me-2' />}
              Search
            </Button>
          </InputGroup>
        </div>

        {error && <div className='container-md w-50'><Alert variant='danger'>{error}</Alert></div>}

        {hasData ? (
          <>
            {/* 1. Raw Data Table */}
            <RenderTableSection title="1. Raw Data" data={rawTableData} />
            
            {/* 2. Logs Table */}
            <RenderTableSection title="2. Logs" data={logsTableData} />
          </>
        ) : (
          <div className='mt-4 text-muted'>
            {status !== 'loading' && status !== 'failed' && 'No logs found. Enter a Sample ID to search.'}
          </div>
        )}
      </div>
    </Wrapper>
  );
};

export default ViewLogs;